import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationPanel = () => {
  const [filter, setFilter] = useState('all');

  const notifications = [
    {
      id: 1,
      type: 'approval',
      title: 'Expense Approval Required',
      message: 'Kadiatou Bah submitted an expense report for $2,500 - Conference attendance',
      time: '15 minutes ago',
      priority: 'high',
      unread: true,
      actionRequired: true,
      relatedUser: 'Kadiatou Bah',
      amount: 2500
    },
    {
      id: 2,
      type: 'task',
      title: 'Overdue Task Alert',
      message: 'Mobile App Redesign milestone is 2 days overdue. Assigned to Sékou Keïta.',
      time: '1 hour ago',
      priority: 'high',
      unread: true,
      actionRequired: true,
      relatedUser: 'Sékou Keïta'
    },
    {
      id: 3,
      type: 'system',
      title: 'Budget Threshold Warning',
      message: 'Engineering department has reached 90% of monthly budget allocation.',
      time: '2 hours ago',
      priority: 'medium',
      unread: true,
      actionRequired: false
    },
    {
      id: 4,
      type: 'approval',
      title: 'Time Off Request',
      message: 'Mariama Condé requested 3 days off from October 15-17, 2024.',
      time: '3 hours ago',
      priority: 'medium',
      unread: false,
      actionRequired: true,
      relatedUser: 'Mariama Condé'
    },
    {
      id: 5,
      type: 'update',
      title: 'Project Status Update',
      message: 'API Optimization project moved to testing phase. 85% complete.',
      time: '4 hours ago',
      priority: 'low',
      unread: false,
      actionRequired: false
    },
    {
      id: 6,
      type: 'system',
      title: 'Performance Review Reminder',
      message: 'Quarterly performance reviews are due in 5 days. 3 reviews pending.',
      time: '6 hours ago',
      priority: 'medium',
      unread: false,
      actionRequired: true
    },
    {
      id: 7,
      type: 'approval',
      title: 'Equipment Purchase Request',
      message: 'Hawa Bangoura requested approval for testing devices worth $3,200.',
      time: '1 day ago',
      priority: 'medium',
      unread: false,
      actionRequired: true,
      relatedUser: 'Hawa Bangoura',
      amount: 3200
    },
    {
      id: 8,
      type: 'update',
      title: 'Team Meeting Scheduled',
      message: 'Weekly team sync scheduled for tomorrow at 10:00 AM.',
      time: '1 day ago',
      priority: 'low',
      unread: false,
      actionRequired: false
    }
  ];

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'approval': return 'CheckCircle';
      case 'task': return 'AlertTriangle';
      case 'system': return 'Settings';
      case 'update': return 'Info';
      default: return 'Bell';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'approval': return 'bg-warning/10 text-warning';
      case 'task': return 'bg-error/10 text-error';
      case 'system': return 'bg-primary/10 text-primary';
      case 'update': return 'bg-success/10 text-success';
      default: return 'bg-muted/10 text-muted-foreground';
    }
  };

  const filteredNotifications = notifications?.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return notification?.unread;
    if (filter === 'action-required') return notification?.actionRequired;
    return notification?.type === filter;
  });

  const unreadCount = notifications?.filter(n => n?.unread)?.length;
  const actionRequiredCount = notifications?.filter(n => n?.actionRequired)?.length;

  const handleNotificationAction = (id, action) => {
    console.log(`${action} notification ${id}`);
    // Notification action logic here
  };

  const markAllAsRead = () => {
    console.log('Marking all notifications as read');
    // Mark all as read logic here
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  return (
    <div className="bg-card border border-border rounded-lg card-shadow">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Icon name="Bell" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">Notifications</h2>
              <p className="text-sm text-muted-foreground">
                {unreadCount} unread, {actionRequiredCount} require action
              </p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={markAllAsRead}
            iconName="CheckCheck"
            iconPosition="left"
          >
            Mark All Read
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
          {[
            { key: 'all', label: 'All', count: notifications?.length },
            { key: 'unread', label: 'Unread', count: unreadCount },
            { key: 'action-required', label: 'Action Required', count: actionRequiredCount },
            { key: 'approval', label: 'Approvals', count: notifications?.filter(n => n?.type === 'approval')?.length }
          ]?.map((tab) => (
            <button
              key={tab?.key}
              onClick={() => setFilter(tab?.key)}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                filter === tab?.key
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab?.label}
              {tab?.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  filter === tab?.key ? 'bg-primary text-primary-foreground' : 'bg-muted-foreground/20'
                }`}>
                  {tab?.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      {/* Notifications List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredNotifications?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No notifications found</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredNotifications?.map((notification) => (
              <div
                key={notification?.id}
                className={`p-4 hover:bg-muted/50 transition-smooth ${
                  notification?.unread ? 'bg-primary/5' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  {/* Notification Icon */}
                  <div className={`p-2 rounded-lg ${getTypeColor(notification?.type)}`}>
                    <Icon name={getNotificationIcon(notification?.type)} size={16} />
                  </div>

                  {/* Notification Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <h4 className={`text-sm font-medium ${
                        notification?.unread ? 'text-foreground' : 'text-muted-foreground'
                      }`}>
                        {notification?.title}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <Icon 
                          name="Clock" 
                          size={12} 
                          className={getPriorityColor(notification?.priority)} 
                        />
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {notification?.time}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                      {notification?.message}
                    </p>

                    {/* Additional Info */}
                    {(notification?.relatedUser || notification?.amount) && (
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground mb-2">
                        {notification?.relatedUser && (
                          <span>User: {notification?.relatedUser}</span>
                        )}
                        {notification?.amount && (
                          <span>Amount: {formatCurrency(notification?.amount)}</span>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    {notification?.actionRequired && (
                      <div className="flex items-center space-x-2">
                        {notification?.type === 'approval' && (
                          <>
                            <Button
                              variant="outline"
                              size="xs"
                              onClick={() => handleNotificationAction(notification?.id, 'approve')}
                              iconName="Check"
                              iconPosition="left"
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outline"
                              size="xs"
                              onClick={() => handleNotificationAction(notification?.id, 'reject')}
                              iconName="X"
                              iconPosition="left"
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {notification?.type === 'task' && (
                          <Button
                            variant="outline"
                            size="xs"
                            onClick={() => handleNotificationAction(notification?.id, 'view')}
                            iconName="ExternalLink"
                            iconPosition="left"
                          >
                            View Task
                          </Button>
                        )}
                        {notification?.type === 'system' && notification?.actionRequired && (
                          <Button
                            variant="outline"
                            size="xs"
                            onClick={() => handleNotificationAction(notification?.id, 'resolve')}
                            iconName="Settings"
                            iconPosition="left"
                          >
                            Resolve
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Unread Indicator */}
                  {notification?.unread && (
                    <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;